<template>
  <div class="meeting-prep tw-bg-gradient-to-r tw-from-purple-100 tw-via-purple-50 tw-to-pink-100 tw-min-h-screen tw-p-8">
    <div class="tw-max-w-6xl tw-mx-auto">
      <!-- Header -->
      <!-- <div class="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6 tw-mb-8">
        <div class="tw-flex tw-justify-between tw-items-center">
          <div>
            <div class="tw-flex tw-items-center">
              <h1 class="tw-text-xl tw-font-bold tw-text-gray-800">
                {{ prospect.name }}
              </h1>
              <span class="tw-ml-2 tw-px-2 tw-py-1 tw-bg-blue-100 tw-text-blue-800 tw-rounded-full tw-text-xs tw-font-semibold">Prospect</span>
            </div>
            <p class="tw-text-sm tw-text-gray-500">
              {{ prospect.email }}
            </p>
          </div>
          <div class="tw-text-right">
            <p class="tw-text-sm tw-font-semibold tw-text-gray-600">
              Meeting Prep
            </p>
            <p
              v-if="primer"
              class="tw-text-sm tw-text-gray-500"
            >
              {{ new Date(primer.created_date).toLocaleDateString() }}
            </p>
          </div>
        </div>
      </div>
 -->
      <!-- Main Content -->
      <div
        v-if="!primer"
        class="tw-flex tw-justify-center tw-items-center tw-h-64"
      >
        <div v-if="!entity?.crm_connection_id && !isGenerating">
          Please link this entity to a CRM contact first
        </div>
        <div v-else-if="entity?.crm_connection_id">
          Generating meeting primer...
        </div>
      </div>
      <div
        v-if="primer"
        class="tw-flex tw-space-x-8"
      >
        <div class="tw-w-3/4">
          <div class="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6">
            <!-- Summary Section -->
            <section class="tw-mb-8">
              <h2 class="title is-3 dm-serif-display">
                Pre-Meeting Overall Summary for <i>{{ entity.name }}</i>
              </h2>
              <h3 class="title is-5 dm-serif-display">
                General Summary
              </h3>
              <p class="tw-text-gray-600 tw-my-4">
                {{ primer.summary }}
              </p>
            </section>

            <!-- Questions Section -->
            <section class="tw-mb-8">
              <h2 class="title is-5 dm-serif-display">
                Suggested Questions
              </h2>
              <ul class="tw-list-disc tw-list-inside tw-text-gray-600 tw-mb-6">
                <li
                  v-for="(question, index) in primer.suggested_questions"
                  :key="index"
                >
                  {{ question }}
                </li>
              </ul>
            </section>

            <!-- Financial Summary Section -->
            <section>
              <h2 class="title is-5 dm-serif-display">
                Financial Summary
              </h2>
              <table class="table is-striped is-hoverable is-fullwidth is-bordered">
                <tbody>
                  <tr
                    v-for="(value, key) in primer.financial_summary"
                    :key="key"
                  >
                    <td class="tw-font-semibold tw-pr-4">
                      {{ key }}
                    </td>
                    <td>{{ value }}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="tw-w-1/4">
          <div class="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6">
            <button
              class="button is-primary is-fullwidth is-outlined"
              :class="{ 'tw-opacity-50 tw-cursor-not-allowed': isGenerating }"
              :disabled="isGenerating"
              @click="generatePrimer"
            >
              {{ isGenerating ? 'Regenerating...' : 'Re-sync from CRM' }}
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="isGenerating"
        class="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6 tw-mt-8 tw-text-center"
      >
        <div class="tw-flex tw-justify-center tw-mb-4">
          <o-loading />
        </div>
        <p class="tw-text-gray-600">
          Generating meeting primer... This may take a few minutes.
        </p>
      </div>
      <StakeModal v-model="showCrmLinkingModal">
        <!-- TODO pass in meeting id -->
        <CrmLinkingModal
          v-if="showCrmLinkingModal"
          :entity-external-id="entityExternalId"
          message="To sync tasks with your CRM, please link this entity to a CRM contact first."
          @contact-linked="handleCrmContactLinked"
          @close="showCrmLinkingModal = false"
        />
      </StakeModal>
    </div>
  </div>
</template>

<script>
import KeyInfoTable from '../components/KeyInfoTable.vue';
import CrmContactSearch from '../components/CrmContactSearch.vue';
import { awaitTask } from '../../checkup/helpers/tasks';
import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import CrmLinkingModal from '../components/CrmLinkingModal.vue';
import StakeModal from '@stakeui/StakeModal.vue';

export default {
  name: 'MeetingPrep',
  components: {
    KeyInfoTable,
    CrmContactSearch,
    CrmLinkingModal,
    StakeModal,
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      isGenerating: false,
      primer: null,
      entity: null,
      pollingInterval: null,
      needsCrmLink: false,
      selectedCrm: null,
      connectedCrms: [],
      wealthboxContacts: [],
      redtailContacts: [],
      isCreatingContacts: false,
      searchTerm: '',
      showCrmLinkingModal: false,
    };
  },
  computed: {
    ...mapStores(useAdvisorStore),
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    currentCrmContacts() {
      return this.selectedCrm === 'Wealthbox' ? this.wealthboxContacts : this.redtailContacts;
    },
  },
  watch: {
    selectedCrm() {
      this.fetchCrmContacts();
    },
  },
  async mounted() {
    try {
      this.connectedCrms = Object.keys(this.advisorStore.activeOAuthConnections)
          .filter(crm => ['Wealthbox', 'Redtail'].includes(crm));
      console.log('Active OAuth Connections:', this.advisorStore.activeOAuthConnections);

      if (this.connectedCrms.length > 0) {
        this.selectedCrm = this.connectedCrms[0]; // Default to the first connected CRM
        await this.fetchCrmContacts();
      }
      await this.fetchEntity();
      await this.fetchPrimer();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      this.loading = false;
    }
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async fetchPrimer() {
      const response = await fetch(`/api/v1/meeting-primer/${this.entityExternalId}/`, {
        headers: this.headers,
      });
      if (response.ok) {
        this.primer = await response.json();
        this.needsCrmLink = false;
      } else if (response.status === 404) {
        this.showCrmLinkingModal = true;
      } else {
        throw new Error('Failed to fetch primer');
      }
    },
    async fetchEntity() {
      const entityResponse = await fetch(`/api/v1/entities/${this.$route.params.entityExternalId}/`);
      const entityData = await entityResponse.json();
      console.log('ENTITY DATA', entityData);
      this.entity = entityData;
    },
    async fetchCrmContacts() {
      if (!this.connectedCrms.includes(this.selectedCrm)) return;
      try {
        const searchParams = new URLSearchParams();
        if (this.searchTerm) {
          searchParams.append('search', this.searchTerm);
        }

        const response = await fetchWrapper(
            `/api/v1/crm/${this.selectedCrm.toLowerCase()}/contacts/?${searchParams.toString()}`,
            {
              headers: this.headers,
            },
        );

        if (response.ok) {
          const data = await response.json();
          if (this.selectedCrm === 'Wealthbox') {
            this.wealthboxContacts = data.contacts;
          } else {
            this.redtailContacts = data.contacts;
          }
        } else {
          console.error(`Failed to fetch ${this.selectedCrm} contacts:`, response.statusText);
        }
      } catch (error) {
        console.error(`Error fetching ${this.selectedCrm} contacts:`, error);
      }
    },
    async generatePrimer() {
      this.isGenerating = true;
      try {
        const response = await awaitTask(`/api/v1/meeting-primer/create/${this.entityExternalId}/`, {
          method: 'POST',
          headers: this.headers,
        });

        if (response.task.status === 'complete') {
          await this.fetchPrimer();
          this.$emit('primer-generated');
        } else {
          console.error('Primer generation failed:', response);
          alert('Failed to generate the meeting primer. Please try again.');
        }
      } catch (error) {
        console.error('Error generating primer:', error);
        alert('An error occurred while generating the meeting primer. Please try again.');
      } finally {
        this.isGenerating = false;
      }
    },
    startPolling() {
      this.pollingInterval = setInterval(() => {
        if (!this.primer && !this.isGenerating) {
          this.fetchPrimer();
        }
      }, 10000); // Poll every 10 seconds
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
    },
    async handleCrmContactLinked(data) {
      this.crmContactId = data.crm_contact_id;
      await this.generatePrimer();
    },
    async handleSearch(searchTerm) {
      if (this.selectedCrm === 'Redtail') {
        this.searchTerm = searchTerm;
        await this.fetchCrmContacts();
      }
    },
  },
};
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
